import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, IconButton, Alert, Switch, FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { databaseEditionsAPI, databaseTypesAPI } from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';
import { DatabaseEdition, DatabaseType } from '../../types';

interface DatabaseEditionFormData { databaseTypeId: number | string; editionName: string; displayOrder: number; isActive: boolean; }

const DatabaseEditions = () => {
  const [editions, setEditions] = useState<DatabaseEdition[]>([]);
  const [databaseTypes, setDatabaseTypes] = useState<DatabaseType[]>([]);
  const [selectedType, setSelectedType] = useState<number | string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEdition, setEditingEdition] = useState<DatabaseEdition | null>(null);
  const [formData, setFormData] = useState<DatabaseEditionFormData>({ databaseTypeId: '', editionName: '', displayOrder: 0, isActive: true });
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; id: number }>({ open: false, id: 0 });

  useEffect(() => { fetchDatabaseTypes(); }, []);

  const fetchEditions = useCallback(async () => {
    if (databaseTypes.length === 0) return;
    try {
      const params = selectedType ? { databaseTypeId: selectedType } : {};
      const response = await databaseEditionsAPI.getAll(params);
      setEditions(response.data); setError('');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
      console.error('Error fetching database editions:', err);
      setError(`Failed to load database editions: ${axiosErr.response?.data?.error || axiosErr.message || 'Unknown error'}`);
    } finally { setLoading(false); }
  }, [selectedType, databaseTypes.length]);

  useEffect(() => { if (databaseTypes.length > 0) { fetchEditions(); } }, [selectedType, databaseTypes.length, fetchEditions]);

  const fetchDatabaseTypes = async () => {
    try {
      const response = await databaseTypesAPI.getAll();
      const vmBasedTypes = response.data.filter((t: DatabaseType) => t.DeploymentType === 'vm_based');
      setDatabaseTypes(vmBasedTypes);
      if (vmBasedTypes.length === 0) { setError('No VM-based database types found. Please add a database type with Deployment Type "Database on VM" first.'); setLoading(false); }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
      console.error('Error fetching database types:', err);
      setError(`Failed to load database types: ${axiosErr.response?.data?.error || axiosErr.message || 'Unknown error'}`);
      setLoading(false);
    }
  };

  const handleOpenDialog = (edition: DatabaseEdition | null = null) => {
    if (edition) { setEditingEdition(edition); setFormData({ databaseTypeId: edition.DatabaseTypeId, editionName: edition.EditionName, displayOrder: edition.DisplayOrder, isActive: !!edition.IsActive }); }
    else { setEditingEdition(null); setFormData({ databaseTypeId: selectedType || '', editionName: '', displayOrder: 0, isActive: true }); }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => { setOpenDialog(false); setEditingEdition(null); setFormData({ databaseTypeId: '', editionName: '', displayOrder: 0, isActive: true }); };

  const handleSubmit = async () => {
    try {
      if (editingEdition) { await databaseEditionsAPI.update(editingEdition.DatabaseEditionId, formData); } else { await databaseEditionsAPI.create(formData); }
      fetchEditions(); handleCloseDialog(); setError('');
    } catch (err: unknown) { const axiosErr = err as { response?: { data?: { error?: string } } }; setError(axiosErr.response?.data?.error || 'Failed to save database edition'); }
  };

  const handleDeleteClick = (id: number) => {
    setConfirmDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    const id = confirmDialog.id;
    setConfirmDialog({ open: false, id: 0 });
    try { await databaseEditionsAPI.delete(id); fetchEditions(); setError(''); } catch (err) { setError('Failed to delete database edition'); }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Database Editions (for VM-based Databases)</Typography>
        <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon fontSize="small" />} onClick={() => handleOpenDialog()} disabled={databaseTypes.length === 0}>Add Edition</Button>
      </Box>
      {databaseTypes.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>No VM-based database types found. Please create a database type with "Database on VM" deployment type first.</Alert>}
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField select label="Filter by Database Type" value={selectedType} onChange={(e) => setSelectedType(e.target.value)} sx={{ minWidth: 250 }} disabled={databaseTypes.length === 0}>
          <MenuItem value="">All Types</MenuItem>
          {databaseTypes.map((type) => (<MenuItem key={type.DatabaseTypeId} value={type.DatabaseTypeId}>{type.DatabaseName}</MenuItem>))}
        </TextField>
      </Paper>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Database Type</TableCell><TableCell>Edition Name</TableCell><TableCell>Display Order</TableCell><TableCell>Active</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
          <TableBody>
            {editions.map((edition) => (
              <TableRow key={edition.DatabaseEditionId}>
                <TableCell>{edition.DatabaseName}</TableCell><TableCell>{edition.EditionName}</TableCell><TableCell>{edition.DisplayOrder}</TableCell><TableCell>{edition.IsActive ? 'Yes' : 'No'}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenDialog(edition)} size="small"><EditIcon fontSize="small" /></IconButton>
                  <IconButton onClick={() => handleDeleteClick(edition.DatabaseEditionId)} size="small" color="error"><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingEdition ? 'Edit Database Edition' : 'Add Database Edition'}</DialogTitle>
        <DialogContent>
          <TextField select margin="dense" size="small" label="Database Type" fullWidth value={formData.databaseTypeId} onChange={(e) => setFormData({ ...formData, databaseTypeId: e.target.value })} required>
            {databaseTypes.map((type) => (<MenuItem key={type.DatabaseTypeId} value={type.DatabaseTypeId}>{type.DatabaseName}</MenuItem>))}
          </TextField>
          <TextField margin="dense" size="small" label="Edition Name" fullWidth value={formData.editionName} onChange={(e) => setFormData({ ...formData, editionName: e.target.value })} required helperText='"Standard", "Enterprise", "Web", "Express"' />
          <TextField margin="dense" size="small" label="Display Order" type="number" fullWidth value={formData.displayOrder} onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })} />
          <FormControlLabel control={<Switch checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />} label="Active" />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} size="small">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="secondary" disabled={!formData.databaseTypeId || !formData.editionName}>{editingEdition ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        title="Deactivate Database Edition"
        message="Are you sure you want to deactivate this database edition?"
        confirmLabel="Deactivate"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ open: false, id: 0 })}
      />
    </Box>
  );
};

export default DatabaseEditions;
